import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  type Note,
  NoteType,
  filenameToDate,
  isCalendarNote,
  isFolder,
} from '../../utils/note'
import { useCreateNote } from '../../hooks/useCreateNote'
import {
  type SidebarEntry,
  createSidebarEntries,
  exportNotes,
  findNoteKey,
} from '../../modules/sidebar/SidebarBuilder'
import { useDevtools } from '../Devtools'
import { cacheKeys } from '../../utils/queryKeyFactory'
import useStarterNotes from '../../hooks/useStarterNotes'
import { useManageSubscription } from '../../hooks/useManageSubscription'
import { trackEvent } from '../../lib/analytics'
import {
  useSelectedRecordName,
  useSelectedRecordNameDispatch,
} from '../SelectedRecordName'
import {
  useSelectedDate,
  useSelectedDateDispatch,
  ACTIVE_PERIOD_TO_PREFIX,
} from '../SelectedDate'
import { useAuthenticatedUser } from '../../providers/UserProvider'
import { cachedNotesContext, getNoteFromAnyCache } from '../CachedNotes'
import {
  usePrivateCalendarNotes,
  usePrivateProjectNotes,
  usePrivateProjectNotesByTitle,
  useTeamCalendarNotes,
  useTeamProjectNotes,
} from '../../hooks/useNotesFactory'
import { useNotification } from '../../providers/NotificationProvider'
import { errorIcon } from '../../components/Notification'
import { v4 as uuid } from 'uuid'
import { useSubscriptionVerification } from '../SubscriptionVerification'
import { SidebarContext, SidebarProviderContextType } from './context'
import { useNoteProcessor } from '../../providers/useNoteProcessor'
import { useImportNotes } from '../../hooks/useImportNotes'
import { useImportFolder } from '../../hooks/useImportFolder'
import { useImportConflictDialog } from '../../components/ImportConflictDialog'
import { triggerFileInput } from '../../utils/fileInputHelper'
import { type ImportSourceType } from '../../utils/import/core'

type Props = {
  onOpenCommandBar: (search: string) => void
  children: ReactNode
}

export function SidebarProvider({ onOpenCommandBar, children }: Props) {
  const { setDialogOpen, isGuest, isTrialRunning, isSubscribed } =
    useSubscriptionVerification()

  // Create a function that calls setDialogOpen with true
  const openPaywall = useCallback(() => {
    setDialogOpen(true)
  }, [setDialogOpen])

  // Get devtools functionality (always call hook, but only use in development)
  const devtoolsHook = useDevtools()
  const devtoolsToggle = import.meta.env.DEV ? devtoolsHook : undefined

  // Markdown import functionality
  const { importFiles } = useImportNotes()
  const { showDialog, DialogComponent: ConflictDialogComponent } =
    useImportConflictDialog()
  const handleImportMarkdownFiles = useCallback(() => {
    triggerFileInput(
      {
        multiple: true,
        accept:
          'text/*,.md,.txt,.markdown,.mdown,.mkd,.org,.rst,.adoc,.asciidoc',
      },
      (files) => {
        void importFiles(files, '', showDialog)
      }
    )
  }, [importFiles, showDialog])

  // Folder import functionality
  const {
    handleImportFolder: importFolder,
    DialogComponent: FolderDialogComponent,
  } = useImportFolder()
  const handleImportFolder = useCallback(
    (sourceType?: ImportSourceType) => {
      triggerFileInput(
        {
          webkitdirectory: true,
        },
        (files) => {
          void importFolder(files, sourceType)
        }
      )
    },
    [importFolder]
  )

  // Determine if the trial is expired but not subscribed
  const isExpired = isTrialRunning === 'expired' && !isSubscribed
  const selectedRecordName = useSelectedRecordName()
  const setSelectedRecordName = useSelectedRecordNameDispatch()
  const { createTeamspaceStarterNotes } = useStarterNotes()
  const user = useAuthenticatedUser()
  const { isLoading: isLoadingPrivateProjectNotes, data: privateProjectNotes } =
    usePrivateProjectNotes()
  const { isLoading: isLoadingTeamProjectNotes, data: teamProjectNotes } =
    useTeamProjectNotes()
  const { data: privateCalendarNotes } = usePrivateCalendarNotes()
  const privateNotes = useMemo(() => {
    return new Map<string, Note>([
      ...(privateProjectNotes?.map ?? []),
      ...(privateCalendarNotes?.map ?? []),
    ])
  }, [privateProjectNotes, privateCalendarNotes])

  const { data: teamCalendarNotes } = useTeamCalendarNotes()
  const teamNotes = useMemo(() => {
    return new Map<string, Note>([
      ...(teamProjectNotes?.map ?? []),
      ...(teamCalendarNotes?.map ?? []),
    ])
  }, [teamProjectNotes, teamCalendarNotes])
  const notes = useMemo(
    () => new Map<string, Note>([...privateNotes, ...teamNotes]),
    [privateNotes, teamNotes]
  )
  const [needsAttention, setNeedsAttention] = useState(false)
  const [shouldForceUpdateEditor, setShouldForceUpdateEditor] = useState(0)

  // Cache of record names that are in special folders (starting with @)
  const specialFolderContentsReference = useRef<Set<string>>(new Set())

  // Function to build cache of notes in special folders
  const buildSpecialFolderCache = useCallback((notesMap: Map<string, Note>) => {
    const cache = new Set<string>()

    // Find all special folders (starting with @)
    const specialFolders = new Set<string>()
    for (const note of notesMap.values()) {
      if (note.filename.startsWith('@') && note.isFolder && note.recordName) {
        specialFolders.add(note.recordName)
        cache.add(note.recordName) // Include the folder itself
      }
    }

    // Recursively find all descendants of special folders
    const findDescendants = (parentId: string) => {
      for (const note of notesMap.values()) {
        if (
          note.parent === parentId &&
          note.recordName &&
          !cache.has(note.recordName)
        ) {
          cache.add(note.recordName)
          if (note.isFolder && note.recordName) {
            findDescendants(note.recordName) // Recursively check subfolders
          }
        }
      }
    }

    // Find all descendants of special folders
    for (const folderId of specialFolders) {
      findDescendants(folderId)
    }

    return cache
  }, [])

  // Update cache when notes change
  useEffect(() => {
    const newCache = buildSpecialFolderCache(notes)
    specialFolderContentsReference.current = newCache
  }, [notes, buildSpecialFolderCache])

  // Utility function to check if a note is in a special folder
  // This function is now stable and won't change when the cache updates
  const isInSpecialFolder = useCallback(
    (recordName: string | undefined) => {
      if (!recordName) return false
      return specialFolderContentsReference.current.has(recordName)
    },
    [] // No dependencies, making this function stable
  )

  const selectedDate = useSelectedDate()
  const selectedDateDispatch = useSelectedDateDispatch()
  const { showNotification, hideNotification } = useNotification()

  const cachedNotesQueryClient = useQueryClient({
    context: cachedNotesContext,
  })

  const handleRevealNote = useCallback(
    (recordName?: string) => {
      if (!recordName) return

      // Try to find the note in all caches (project and calendar notes)
      const note = getNoteFromAnyCache(
        cachedNotesQueryClient,
        user.userId,
        recordName
      )

      if (note) {
        if (isCalendarNote(note.noteType)) {
          const { date, timeframe } = filenameToDate(note.filename)
          // eslint-disable-next-line unicorn/no-null -- checking for both null and undefined
          if (date == null) {
            const errorDetails = {
              message: 'Error: Invalid note filename',
              description: "Calendar note doesn't contain a valid date",
              filename: note.filename,
              noteType: note.noteType,
            }

            showNotification({
              title: errorDetails.message,
              message: errorDetails.description,
              icon: errorIcon,
            })

            trackEvent(
              'WEB - SidebarProvider.handleRevealNote.error',
              errorDetails
            )
            return
          }

          // For calendar notes, construct the record name and use unified selection
          const prefix = ACTIVE_PERIOD_TO_PREFIX[timeframe ?? 'day']

          const calendarRecordName = note.parent
            ? `${prefix}_${note.parent}`
            : prefix

          // First set the calendar record name to get the right timeframe/teamspace
          setSelectedRecordName(calendarRecordName)

          // Then set the specific date within that timeframe
          switch (timeframe) {
            case 'day': {
              selectedDateDispatch({
                type: 'setDay',
                date,
                teamspace: note.parent ?? undefined,
              })
              break
            }
            case 'week': {
              selectedDateDispatch({
                type: 'setWeek',
                week: date.week(),
                year: date.year(),
                teamspace: note.parent ?? undefined,
              })
              break
            }
            case 'month': {
              selectedDateDispatch({
                type: 'setMonth',
                month: date.month(),
                year: date.year(),
                teamspace: note.parent ?? undefined,
              })
              break
            }
            case 'quarter': {
              selectedDateDispatch({
                type: 'setQuarter',
                quarter: date.quarter(),
                year: date.year(),
                teamspace: note.parent ?? undefined,
              })
              break
            }
            case 'year': {
              selectedDateDispatch({
                type: 'setYear',
                year: date.year(),
                teamspace: note.parent ?? undefined,
              })
              break
            }
          }
        } else if (!isFolder(note)) {
          // For project notes, use unified selection
          setSelectedRecordName(recordName)
        }

        // Only scroll to notes that are already visible in the sidebar
        setNeedsAttention(true)
      }
    },
    [
      cachedNotesQueryClient,
      user.userId,
      setSelectedRecordName,
      showNotification,
      selectedDateDispatch,
    ]
  )

  const { createPrivateStarterNotes: createStarterNotes } = useStarterNotes()
  const [isCreatingStarterNotes, setIsCreatingStarterNotes] = useState(false)

  useEffect(() => {
    if (!isCreatingStarterNotes) {
      setIsCreatingStarterNotes(true)

      void createStarterNotes(
        () => {
          // Started
          showNotification({
            title: 'Welcome to NotePlan',
            message: 'Setting you up for a productive start...',
            icon: 'fa-sun text-orange-400',
          })
        },
        () => {
          // Ready for open daily note
          selectedDateDispatch({ type: 'today', forceDay: true })

          // This is necessary so that we reload the cached notes inside the TipTap editor object, otherwise clicking on "Start Here" will create a new note instead of opening the existing
          setShouldForceUpdateEditor((previous) => previous + 1)
        }
      ).finally(() => {
        setIsCreatingStarterNotes(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once on mount
  }, [])

  const createNoteMutation = useCreateNote()

  const createTeamSpace = useCallback(() => {
    const recordName = uuid()
    createNoteMutation.mutate(
      {
        recordName,
        noteType: NoteType.TEAM_SPACE,
        // eslint-disable-next-line unicorn/no-null -- supabase needs null
        parent: null,
        isDir: true,
      },
      {
        onSuccess: () => {
          showNotification({
            title: 'Teamspace',
            message: 'Setting up your teamspace...',
            icon: 'fa-cube text-green-400',
          })

          void createTeamspaceStarterNotes(recordName, () => {
            // Open today's note of the teamspace, now with the updated caches
            selectedDateDispatch({
              type: 'setDay',
              date: dayjs(),
              teamspace: recordName,
            })
          })
        },
      }
    )
  }, [
    createNoteMutation,
    createTeamspaceStarterNotes,
    selectedDateDispatch,
    showNotification,
  ])

  const createFolder = useCallback(
    // eslint-disable-next-line unicorn/no-null -- supabase needs null
    (noteType = NoteType.PROJECT_NOTE, parent: string | null = null) => {
      const recordName = uuid()
      createNoteMutation.mutate(
        {
          recordName,
          noteType,
          parent,
          isDir: true,
        },
        {
          onSuccess: (note: Note) => {
            if (note.recordName && !note.filename.startsWith('@')) {
              handleRevealNote(note.recordName)
            }
          },
        }
      )
    },
    [createNoteMutation, handleRevealNote]
  )

  const createNote = useCallback(
    (
      noteType = NoteType.PROJECT_NOTE,
      // eslint-disable-next-line unicorn/no-null -- supabase needs null
      parent: string | null = null,
      isTemplate = false
    ) => {
      const recordName = uuid()
      const templateContent = isTemplate
        ? `---
type: empty-note
---
# `
        : undefined

      createNoteMutation.mutate(
        {
          recordName,
          noteType,
          parent,
          content: templateContent,
        },
        {
          onSuccess: (note: Note) => {
            if (note.recordName) {
              handleRevealNote(note.recordName)
            }
          },
        }
      )
    },
    [createNoteMutation, handleRevealNote]
  )

  const { openManageSubscription } = useManageSubscription()

  // function wrapped in useCallback to initiate the export of a note and it's children
  const handleExportSupabaseTeamspace = useCallback(
    (recordName: string) => {
      const entry = teamNotes.get(recordName)

      if (entry?.recordName) {
        showNotification({
          title: 'Exporting attachments...',
          message: 'Downloading, this can take a while.',
          icon: 'fa-download text-green-400',
          autoHide: false,
        })
        void exportNotes(teamNotes, entry.recordName)
          .then(() => {
            hideNotification()
          })
          .catch((error: unknown) => {
            hideNotification()
            showNotification({
              title: 'Export Error',
              message:
                error instanceof Error
                  ? error.message
                  : 'Failed to export notes',
              icon: errorIcon,
            })
          })
      } else {
        trackEvent('web.SidebarProvider.error', {
          message: 'teamspace note not found, this should not happen',
          recordName,
        })
      }
    },
    [hideNotification, showNotification, teamNotes]
  )

  const handleExportPrivateNotes = useCallback(() => {
    showNotification({
      title: 'Exporting private notes...',
      message: 'Downloading, this can take a while.',
      icon: 'fa-download text-green-400',
      autoHide: false,
    })

    void exportNotes(privateNotes)
      .then(() => {
        hideNotification()
      })
      .catch((error: unknown) => {
        hideNotification()
        showNotification({
          title: 'Export Error',
          message:
            error instanceof Error ? error.message : 'Failed to export notes',
          icon: errorIcon,
        })
      })
  }, [hideNotification, showNotification, privateNotes])

  // ensure the @Templates, @Archive, and @Trash folders exist
  const { data: privateProjectNotesByTitle } = usePrivateProjectNotesByTitle()
  const { createFolder: createFolderInBackground } = useNoteProcessor()

  // Track which special folders are currently being created to prevent duplicates
  const pendingFolderCreationsReference = useRef<Set<string>>(new Set())
  const hasAttemptedFolderCreationReference = useRef(false)

  // Create @Templates, @Archive and @Trash folders if they don't exist
  // Note: If duplicates exist, they will be handled by native apps through ID updates
  useEffect(() => {
    // Only proceed if notes are fully loaded and we haven't already attempted creation
    if (
      !isLoadingPrivateProjectNotes &&
      privateProjectNotesByTitle &&
      privateProjectNotes &&
      !hasAttemptedFolderCreationReference.current
    ) {
      // Mark that we've attempted creation to prevent re-runs
      hasAttemptedFolderCreationReference.current = true

      const templatesFolders =
        privateProjectNotesByTitle.get('@Templates') ?? []
      const archiveFolders = privateProjectNotesByTitle.get('@Archive') ?? []
      const trashFolders = privateProjectNotesByTitle.get('@Trash') ?? []

      const foldersToCreate = []

      // Handle @Templates folders
      if (
        templatesFolders.length === 0 &&
        !pendingFolderCreationsReference.current.has('@Templates')
      ) {
        pendingFolderCreationsReference.current.add('@Templates')
        foldersToCreate.push({
          type: 'createFolder' as const,
          filename: '@Templates',
          title: '@Templates',
          noteType: NoteType.PROJECT_NOTE,
          // eslint-disable-next-line unicorn/no-null -- supabase needs null
          parent: null,
        })
      }

      // Handle @Archive folders
      if (
        archiveFolders.length === 0 &&
        !pendingFolderCreationsReference.current.has('@Archive')
      ) {
        pendingFolderCreationsReference.current.add('@Archive')
        foldersToCreate.push({
          type: 'createFolder' as const,
          filename: '@Archive',
          title: '@Archive',
          noteType: NoteType.PROJECT_NOTE,
          // eslint-disable-next-line unicorn/no-null -- supabase needs null
          parent: null,
        })
      }

      // Handle @Trash folders
      if (
        trashFolders.length === 0 &&
        !pendingFolderCreationsReference.current.has('@Trash')
      ) {
        pendingFolderCreationsReference.current.add('@Trash')
        foldersToCreate.push({
          type: 'createFolder' as const,
          filename: '@Trash',
          title: '@Trash',
          noteType: NoteType.PROJECT_NOTE,
          // eslint-disable-next-line unicorn/no-null -- supabase needs null
          parent: null,
        })
      }

      if (foldersToCreate.length > 0) {
        createFolderInBackground(
          foldersToCreate,
          'Creating special folders (@Templates, @Archive, @Trash)'
        )
      }
    }
  }, [
    isLoadingPrivateProjectNotes,
    privateProjectNotesByTitle,
    privateProjectNotes,
    createFolderInBackground,
  ])

  const sidebarEntries: SidebarEntry[] = useMemo(
    () =>
      createSidebarEntries(
        user,
        isLoadingPrivateProjectNotes,
        privateNotes,
        isLoadingTeamProjectNotes,
        teamNotes,
        createTeamSpace,
        createFolder,
        createNote,
        onOpenCommandBar,
        openManageSubscription,
        openPaywall,
        isGuest,
        isExpired,
        handleExportPrivateNotes,
        handleImportMarkdownFiles,
        handleImportFolder,
        devtoolsToggle
      ),
    [
      user,
      isLoadingPrivateProjectNotes,
      privateNotes,
      isLoadingTeamProjectNotes,
      teamNotes,
      createTeamSpace,
      createFolder,
      createNote,
      onOpenCommandBar,
      openManageSubscription,
      openPaywall,
      isGuest,
      isExpired,
      handleExportPrivateNotes,
      handleImportMarkdownFiles,
      handleImportFolder,
      devtoolsToggle,
    ]
  )
  const noteKey = useMemo(
    () => findNoteKey(sidebarEntries, notes, selectedRecordName, selectedDate),
    [sidebarEntries, notes, selectedRecordName, selectedDate]
  )

  const handleSelectRecordName = useCallback(
    (recordName: string) => {
      // For all cases, just use setSelectedRecordName - it handles both calendar and project notes
      setSelectedRecordName(recordName)
    },
    [setSelectedRecordName]
  )

  useEffect(() => {
    // check to make sure we use the new map data structure
    if (privateProjectNotes && Array.isArray(privateProjectNotes)) {
      void cachedNotesQueryClient.invalidateQueries({
        queryKey: cacheKeys.privateNotes(),
        refetchType: 'all',
      })
    }
    if (teamProjectNotes && Array.isArray(teamProjectNotes)) {
      void cachedNotesQueryClient.invalidateQueries({
        queryKey: cacheKeys.teamNotes(),
        refetchType: 'all',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // scroll to the selected record name (only if visible)
  useEffect(() => {
    if (needsAttention) {
      try {
        const element = document.querySelector(`#${selectedRecordName}`)
        // Only scroll if the element exists and is visible (not in a closed folder)
        // eslint-disable-next-line unicorn/no-null -- null is a valid value for offsetParent
        if (element && (element as HTMLElement).offsetParent != null) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        // If element is not visible, silently fail (no error or notification)
      } catch (error) {
        console.warn('Failed to scroll to element:', error)
      }
      setNeedsAttention(false)
    }
  }, [selectedRecordName, needsAttention])

  // performance optimization
  const context = useMemo(
    () =>
      ({
        noteKey,
        handleExportSupabaseTeamspace,
        handleSelectRecordName,
        handleRevealNote,
        privateProjectNotes: privateProjectNotes?.map,
        privateCalendarNotes: privateCalendarNotes?.map,
        privateNotes,
        teamProjectNotes: teamProjectNotes?.map,
        teamCalendarNotes: teamCalendarNotes?.map,
        teamNotes,
        notes,
        sidebarEntries,
        createFolder,
        createNote,
        shouldForceUpdateEditor,
        isInSpecialFolder,
      }) as SidebarProviderContextType,
    [
      noteKey,
      handleExportSupabaseTeamspace,
      handleSelectRecordName,
      handleRevealNote,
      privateProjectNotes,
      privateCalendarNotes,
      privateNotes,
      teamProjectNotes,
      teamCalendarNotes,
      teamNotes,
      notes,
      sidebarEntries,
      createFolder,
      createNote,
      shouldForceUpdateEditor,
      isInSpecialFolder,
    ]
  )
  return (
    <SidebarContext.Provider value={context}>
      {children}
      {ConflictDialogComponent}
      {FolderDialogComponent}
    </SidebarContext.Provider>
  )
}
